import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import {
  SortAscending,
  SortDescending,
  ArrowsDownUp
} from '@phosphor-icons/react';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortOption {
  key: string;
  label: string;
  ascendingLabel?: string; // Custom label for ascending (e.g., "First" for dates)
  descendingLabel?: string; // Custom label for descending (e.g., "Last" for dates)
}

interface SortIconDropdownProps {
  options: SortOption[];
  currentSort?: string;
  currentDirection?: SortDirection;
  onSortChange: (key: string, direction: SortDirection) => void;
  size?: 'small' | 'medium' | 'large';
}

export const SortIconDropdown: React.FC<SortIconDropdownProps> = ({
  options,
  currentSort,
  currentDirection,
  onSortChange,
  size = 'medium'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSortSelect = (key: string, direction: SortDirection) => {
    onSortChange(key, direction);
    handleClose();
  };

  const getSortIcon = () => {
    if (!currentSort || !currentDirection) {
      return <ArrowsDownUp size={isMobile ? 20 : 18} />;
    }
    return currentDirection === 'asc' 
      ? <SortAscending size={isMobile ? 20 : 18} />
      : <SortDescending size={isMobile ? 20 : 18} />;
  };

  const getCurrentSortLabel = () => {
    if (!currentSort) return 'Sort';
    const option = options.find(opt => opt.key === currentSort);
    if (!option) return 'Sort';
    
    const directionText = currentDirection === 'asc' ? '↑' : currentDirection === 'desc' ? '↓' : '';
    return `${option.label} ${directionText}`;
  };

  return (
    <>
      <Tooltip title={getCurrentSortLabel()}>
        <IconButton
          onClick={handleClick}
          size={size}
          className={isMobile ? 'mobile-touch-target' : ''}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 500,
            height: 40,
            width: 40,
            minWidth: isMobile ? '44px' : 40,
            minHeight: isMobile ? '44px' : 40,
            px: 0,
            border: '1px solid #e1e5e9',
            backgroundColor: currentSort && currentDirection ? 'rgba(75, 94, 219, 0.04)' : 'white',
            color: currentSort && currentDirection ? '#4B5EDB' : '#6b7280',
            '&:hover': {
              backgroundColor: currentSort && currentDirection ? 'rgba(75, 94, 219, 0.08)' : '#f8f9fa',
              borderColor: currentSort && currentDirection ? '#4B5EDB' : '#d1d5db'
            }
          }}
        >
          {getSortIcon()}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 200,
            maxHeight: 300,
            borderRadius: isMobile ? 2 : 1,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        {options.map((option) => [
          <MenuItem
            key={`${option.key}-asc`}
            onClick={() => handleSortSelect(option.key, 'asc')}
            selected={currentSort === option.key && currentDirection === 'asc'}
            sx={{ py: isMobile ? 1.5 : 1 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <SortAscending size={16} />
            </ListItemIcon>
            <ListItemText 
              primary={`${option.label} (${option.ascendingLabel || 'A-Z'})`} 
            />
          </MenuItem>,
          <MenuItem
            key={`${option.key}-desc`}
            onClick={() => handleSortSelect(option.key, 'desc')}
            selected={currentSort === option.key && currentDirection === 'desc'}
            sx={{ py: isMobile ? 1.5 : 1 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <SortDescending size={16} />
            </ListItemIcon>
            <ListItemText 
              primary={`${option.label} (${option.descendingLabel || 'Z-A'})`} 
            />
          </MenuItem>
        ])}
        
        {/* Clear sort option */}
        {currentSort && currentDirection && (
          <MenuItem
            onClick={() => handleSortSelect('', null)}
            sx={{ 
              py: isMobile ? 1.5 : 1,
              borderTop: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.secondary
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <ArrowsDownUp size={16} />
            </ListItemIcon>
            <ListItemText primary="Clear sort" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
